import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Magnetic as Mi } from '../../styles';
const divideRem = (spacer, divideBy) => `
	${parseFloat(spacer.slice(0, -3)) / divideBy}rem
`;
export const StyledButton = styled('button') `
	position: relative;
	display: inline-flex;
	justify-content: center;
	align-items: center;
	min-height: ${Mi.buttonBaseMinHeight};
	padding-left: ${(props) => (props.icon || props.isBusy) && `${Mi.spacer40}`}; // apply padding if icon present, or if busy
	padding-right: ${(props) => props.iconRight && `${Mi.spacer40}`}; // apply padding if icon is present on the right
	transition: all 150ms;
`;
// ButtonStyle.Flat isHoverEffectDisabled prop is used in place of having something like ButtonStyle.Link
export const StyledFlatButton = styled(StyledButton) `
	width: auto;
	font-size: ${Mi.baseFontSize};
	font-family: ${Mi.baseFontFamily};
	color: ${Mi.colorBrand50};
	min-height: ${Mi.buttonBaseMinHeight};
	cursor: ${Mi.buttonBaseCursor};
	&:hover {
		box-shadow: none;
		outline: ${Mi.buttonBaseOutlineFocus};
		color: ${(props) => props.isHoverEffectDisabled && Mi.colorBrand40};
		background-color: ${(props) => !props.isHoverEffectDisabled && Mi.buttonFlatBackgroundColorHover};
	}
	&:active {
		color: ${(props) => props.isHoverEffectDisabled && Mi.colorBrand60};
		background-color: ${(props) => !props.isHoverEffectDisabled && Mi.buttonFlatBackgroundColorActive};
	}
	&:disabled {
		background: transparent;
		color: ${Mi.disabledFontColor};
		cursor: ${Mi.disabledCursor};
	}

	@supports not selector(: focus-visible) {
		&:focus {
			outline: ${Mi.baseBorderWidth} solid ${Mi.colorTransparentUltrablue75};
		}
	}
	&:focus-visible {
		outline: ${Mi.baseBorderWidth} solid ${Mi.colorTransparentUltrablue75};
	}
`;
export const StyledFlatCompactButton = styled(StyledFlatButton) `
	min-height: ${Mi.fontSize28};
	padding: ${divideRem(Mi.spacer04, 2)}
		${(props) => (!!props.iconRight ? Mi.spacer32 : divideRem(Mi.spacer12, 2))}
		${divideRem(Mi.spacer04, 2)}
		${(props) => (!!props.icon ? Mi.spacer40 : divideRem(Mi.spacer12, 2))};
	user-select: none;
	background: none;
	color: ${Mi.buttonFlatFontColor};
	border: none;
	width: auto;
	text-align: left;
	outline: 0px;
	margin-bottom: 0.5rem;
	border-radius: ${Mi.baseBorderRadius};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;

	min-width: 0px;
	&:hover {
		background-color: ${Mi.colorTransparentBlack10};
	}
	&:active {
		background-color: ${Mi.colorTransparentBlack15};
	}
`;
export const miIconButtonBase = (props) => css `
	height: ${Mi.buttonBaseMinHeight};
	font-size: ${props.fontSize ? `${props.fontSize}px` : Mi.buttonBaseFontSize};
	cursor: pointer;
	padding: ${props.padding ? props.padding : Mi.buttonBasePadding};
	border: ${Mi.buttonBorderWidth} ${Mi.buttonBorderStyle};
	border-radius: ${Mi.baseBorderRadius};
	color: ${Mi.buttonFlatFontColor};
	background-color: transparent;
	&:hover {
		background-color: ${!props.disabled && Mi.buttonFlatBackgroundColorHover};
	}
	&:active,
	&.is-open {
		background-color: ${!props.disabled && Mi.buttonFlatBackgroundColorActive};
	}
	// disabled state
	background: ${props.disabled && Mi.buttonDisabledBackgroundColor};
	color: ${props.disabled && Mi.buttonDisabledFontColor};
	cursor: ${props.disabled && Mi.buttonDisabledCursor};
`;
export const StyledIconButton = styled('button') `
	${miIconButtonBase}
	border: none;
	height: ${Mi.buttonBaseMinHeight};
`;
export const StyledSpinner = styled('span') `
	position: absolute;
	top: 10px;
	left: ${(props) => (props.isFlat ? '14px' : '12px')};
`;
export const StyledLeftIcon = styled('span') `
	position: absolute;
	top: ${(props) => (props.isFlat ? '11px' : '10px')};
	left: ${(props) => (props.iconName === 'mi-icon-upload3' ? '16px' : '20px')};
`;
export const StyledRightIcon = styled('span') `
	position: absolute;
	top: 12px;
	right: ${(props) => (props.isFlat ? '18px' : '12px')};
	font-size: ${Mi.buttonRightIconFontSize};
`;
export const StyledSearchButton = styled('button') `
    ${miIconButtonBase}
    font-size: ${Mi.baseFontSize};
    padding-right: ${Mi.spacer08};
    padding-left: ${Mi.spacer08};
    background-color: ${Mi.buttonSearchBackgroundColor};
    border: ${Mi.buttonSearchBorder};
    border-top-left-radius: ${Mi.searchFieldButtonBorderTopLeftRadius};
    border-bottom-left-radius: ${Mi.searchFieldButtonBorderBottomLeftRadius};
    border-left: ${Mi.searchFieldButtonBorderLeft};
    height: ${Mi.buttonSearchHeight};
    width: ${Mi.buttonSearchWidth};
    flex: none;
`;
export const StyledTransparentWhiteButton = styled('button') `
	${miIconButtonBase}
	font-weight: ${Mi.buttonFontWeight};
	color: ${Mi.colorWhite};
	background-color: ${Mi.buttonTransparentWhiteBackgroundColor};
	border-color: ${Mi.buttonTransparentWhiteBorderColor};
	&:hover {
		background-color: ${Mi.buttonTransparentWhiteBackgroundColorHover};
	}
	&:active {
		background-color: ${Mi.buttonTransparentWhiteBackgroundColorActive};
	}
	&:focus {
		box-shadow: ${Mi.buttonTransparentWhiteBoxShadowFocus};
	}
`;
export const StyledTransparentBlueButton = styled(StyledButton) `
	font-weight: ${Mi.buttonFontWeight};
	color: ${Mi.buttonTransparentBlueColor};
	background-color: ${Mi.buttonTransparentBlueBackgroundColor};
	border-color: ${Mi.buttonTransparentBlueBorderColor};
	&:hover {
		background-color: ${Mi.buttonTransparentBlueBackgroundColorHover};
	}
	&:active {
		background-color: ${Mi.buttonTransparentBlueBackgroundColorActive};
	}
	&:focus {
		box-shadow: ${Mi.buttonTransparentBlueBoxShadowFocus};
	}

	&:disabled {
		background-color: ${Mi.buttonDisabledBackgroundColor};
		color: ${Mi.buttonDisabledFontColor};
		border-color: ${Mi.buttonDisabledBorderColor};
	}
`;
//# sourceMappingURL=button.style.js.map